import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { inviteLinkService, authService } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';

/**
 * Página para processar link de convite ao grupo.
 */
const JoinGroupPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviteInfo, setInviteInfo] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [hasAttemptedAccept, setHasAttemptedAccept] = useState(false);
  
  // Verifica se o usuário está logado
  const currentUser = authService.getCurrentUser();

  const handleAcceptInvite = async () => {
    if (hasAttemptedAccept) return; // Evita múltiplas tentativas
    
    try {
      setHasAttemptedAccept(true);
      setProcessing(true);
      setError('');
      const group = await inviteLinkService.acceptByToken(token);
      
      // Redireciona para o grupo
      navigate(`/groups/${group.id}`, {
        state: { message: `Você entrou no grupo ${group.name}! 🎉` }
      });
    } catch (err) {
      // Apenas loga se não for erro de autenticação
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        console.error('Erro ao aceitar convite:', err);
      }
      
      // Se for erro 401 ou 403, o usuário precisa fazer login novamente
      if (err.response?.status === 401 || err.response?.status === 403) {
        // Não mostra erro, apenas redireciona silenciosamente
        localStorage.setItem('pendingInviteToken', token);
        localStorage.removeItem('user');
        navigate('/login');
      } else {
        setError(err.response?.data?.message || err.response?.data?.detail || 'Erro ao ingressar no grupo.');
        setProcessing(false);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const loadInviteInfo = async () => {
      try {
        setLoading(true);
        setError('');
        const info = await inviteLinkService.getInfo(token);
        
        if (!info.isValid) {
          setError('Este link de convite não é mais válido.');
          setLoading(false);
          return;
        }

        setInviteInfo(info);

        // Verifica se o usuário está logado no momento da carga
        const currentUser = authService.getCurrentUser();
        if (currentUser && currentUser.token && !hasAttemptedAccept) {
          // Usuário está logado - tenta aceitar automaticamente
          await handleAcceptInvite();
        } else {
          // Usuário não está logado - apenas mostra a página de convite
          setLoading(false);
        }
      } catch (err) {
        console.error('Erro ao carregar informações do convite:', err);
        setError(err.response?.data?.message || err.response?.data?.detail || 'Link de convite inválido ou expirado.');
        setLoading(false);
      }
    };

    loadInviteInfo();
  }, [token, navigate]); // Apenas token e navigate como dependências



  const handleLoginRedirect = () => {
    // Salva o token no localStorage para redirecionar após login
    localStorage.setItem('pendingInviteToken', token);
    navigate('/login');
  };

  const handleRegisterRedirect = () => {
    // Salva o token no localStorage para redirecionar após registro
    localStorage.setItem('pendingInviteToken', token);
    navigate('/register');
  };

  if (loading) {
    return <LoadingSpinner message="Carregando convite..." />;
  }

  if (error) {
    return (
      <div style={{ 
        maxWidth: 500, 
        margin: '80px auto', 
        padding: 24,
        textAlign: 'center'
      }}>
        <div style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #EF4444, #DC2626)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          fontSize: 40,
        }}>
          <i className="fa-solid fa-exclamation-triangle" style={{ color: 'white' }}></i>
        </div>
        
        <h1 style={{ marginBottom: 16, fontSize: 24 }}>Link Inválido</h1>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
          {error}
        </p>
        
        <Link to="/groups" className="btn btn-primary">
          <i className="fa-solid fa-users"></i>
          Ver Meus Grupos
        </Link>
      </div>
    );
  }

  if (processing) {
    return <LoadingSpinner message="Ingressando no grupo..." />;
  }

  return (
    <div style={{ 
      maxWidth: 500, 
      margin: '80px auto', 
      padding: 24,
      textAlign: 'center'
    }}>
      <div className="card" style={{ padding: 32 }}>
        {/* Avatar do grupo */}
        <div style={{
          width: 100,
          height: 100,
          borderRadius: 20,
          background: 'var(--primary-gradient)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          fontSize: 48,
          fontWeight: 700,
          color: 'white',
          textTransform: 'uppercase',
        }}>
          {inviteInfo?.groupName?.substring(0, 2)}
        </div>

        <h1 style={{ marginBottom: 12, fontSize: 28 }}>
          Você foi convidado!
        </h1>
        
        <p style={{ 
          color: 'var(--text-secondary)', 
          marginBottom: 8,
          fontSize: 18,
          fontWeight: 600,
        }}>
          {inviteInfo?.groupName}
        </p>

        <p style={{ 
          color: 'var(--text-muted)', 
          marginBottom: 32,
          fontSize: 14,
        }}>
          Convite criado por {inviteInfo?.createdByName}
        </p>

        {/* Informações do convite */}
        <div style={{
          background: 'var(--bg-secondary)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 32,
          textAlign: 'left',
        }}>
          {inviteInfo?.maxUses && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              marginBottom: 8,
              fontSize: 14,
            }}>
              <span style={{ color: 'var(--text-secondary)' }}>
                <i className="fa-solid fa-users" style={{ marginRight: 8 }}></i>
                Usos
              </span>
              <span style={{ fontWeight: 600 }}>
                {inviteInfo.currentUses} / {inviteInfo.maxUses}
              </span>
            </div>
          )}
          
          {inviteInfo?.expiresAt && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              fontSize: 14,
            }}>
              <span style={{ color: 'var(--text-secondary)' }}>
                <i className="fa-solid fa-clock" style={{ marginRight: 8 }}></i>
                Expira em
              </span>
              <span style={{ fontWeight: 600 }}>
                {new Date(inviteInfo.expiresAt).toLocaleDateString('pt-BR')}
              </span>
            </div>
          )}
        </div>

        {!currentUser ? (
          <>
            <p style={{ 
              marginBottom: 24, 
              color: 'var(--text-secondary)',
              fontSize: 14,
            }}>
              Você precisa estar logado para ingressar no grupo
            </p>
            
            <div style={{ 
              display: 'flex', 
              gap: 12, 
              flexDirection: 'column',
            }}>
              <button
                onClick={handleLoginRedirect}
                className="btn btn-primary"
                style={{ width: '100%' }}
              >
                <i className="fa-solid fa-sign-in-alt"></i>
                Fazer Login
              </button>
              
              <button
                onClick={handleRegisterRedirect}
                className="btn btn-outline-primary"
                style={{ width: '100%' }}
              >
                <i className="fa-solid fa-user-plus"></i>
                Criar Conta
              </button>
            </div>
          </>
        ) : (
          <button
            onClick={handleAcceptInvite}
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={processing}
          >
            {processing ? (
              <><i className="fa-solid fa-spinner fa-spin"></i> Ingressando...</>
            ) : (
              <><i className="fa-solid fa-check"></i> Ingressar no Grupo</>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default JoinGroupPage;
